/** @jsxImportSource @emotion/react */
import { EuiBreadcrumb, EuiIcon } from "@inscopix/ideas-eui";
import { useRouteMapContext } from "providers/RouteMapProvider/RouteMapProvider";
import { css } from "@emotion/react";
import { HeaderBreadcrumbs } from "./HeaderBreadcrumbs";
import { ToolDetails } from "pages/tool/PageToolQuery";

interface ToolBreadcrumbsProps {
  tool: ToolDetails;
  currentTenant: { name: string; key: string };
  inDraftMode: boolean;
  invalidateToolQuery?: () => void;
}

const styles = {
  tenantBreadcrumb: css`
    color: #ccc;
    font-weight: 400;
  `,
  toolBreadcrumb: (inDraftMode: boolean) => css`
    color: ${inDraftMode ? "#CCC" : "white"};
    font-weight: ${inDraftMode ? "400" : "700"};
  `,
  draftBreadcrumb: css`
    color: white;
    font-weight: 700;
  `,
};

/*
 * Displays breadcrumbs for the tool pages header
 */
export const ToolBreadcrumbs = ({
  tool,
  currentTenant,
  inDraftMode,
  invalidateToolQuery,
}: ToolBreadcrumbsProps) => {
  const routeMap = useRouteMapContext((s) => s.routeMap);

  const draftTableBreadcrumb: EuiBreadcrumb | undefined = inDraftMode
    ? {
        text: (
          <span>
            <EuiIcon type={"documentEdit"} /> Draft{" "}
          </span>
        ),
        css: styles.draftBreadcrumb,
      }
    : undefined;

  const breadcrumbs: EuiBreadcrumb[] = [];
  breadcrumbs.push({
    text: currentTenant.name,
    onClick: (e) => {
      routeMap["ORGANIZATION"]
        .dynamicPath({
          tenantKey: currentTenant.key,
        })
        .navigateTo();
    },
    "data-test-subj": "toolBreadcrumbsOrganization",
    css: styles.tenantBreadcrumb,
  });

  breadcrumbs.push({
    text: "Tools",
    onClick: (e) => {
      routeMap["ORGANIZATION_TOOLS"]
        .dynamicPath({
          tenantKey: currentTenant.key,
        })
        .navigateTo();
    },
    "data-test-subj": "toolBreadcrumbsTools",
    css: styles.tenantBreadcrumb,
  });

  breadcrumbs.push({
    text: <span>{tool.name}</span>,
    onClick: (e) => {
      invalidateToolQuery?.();
      routeMap["TOOL"]
        .dynamicPath({
          tenantKey: currentTenant.key,
          toolId: tool.id,
        })
        .navigateTo();
    },
    css: styles.toolBreadcrumb(inDraftMode),
    "data-test-subj": "toolBreadcrumbsTool",
  });
  if (draftTableBreadcrumb !== undefined && inDraftMode) {
    breadcrumbs.push(draftTableBreadcrumb);
  }
  return <HeaderBreadcrumbs breadcrumbs={breadcrumbs} />;
};
